import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaTimes, 
  FaSpinner,
  FaImage
} from 'react-icons/fa';
import { patentsAPI, uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';
import { usePortfolio } from '../../context/PortfolioContext';

const PatentsEditor = () => {
  const { portfolioData, addPatent, updatePatent, deletePatent } = usePortfolio();
  // Remove local patents state and use context directly
  const patents = portfolioData?.patents || [];
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingPatent, setEditingPatent] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPatent, setNewPatent] = useState({
    title: '',
    description: '',
    image_url: '',
    date: '',
    authors: [],
    visible: true
  });
  const [toggleLoading, setToggleLoading] = useState({});

  useEffect(() => {
    // setPatents(portfolioData?.patents || []); // This line is removed
  }, [portfolioData]);

  const handleAddPatent = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const newItem = {
        ...newPatent,
        id: newPatent.id || Date.now(),
        visible: newPatent.visible
      };
      const result = await addPatent(newItem);
      if (result.success) {
        setNewPatent({ title: '', description: '', image_url: '', date: '', authors: [], visible: true });
        setShowAddForm(false);
        setMessage('Patent added successfully!');
      } else {
        setMessage(result.error || 'Error adding patent');
      }
    } catch (error) {
      setMessage('Error adding patent');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePatent = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const newItem = {
        ...editingPatent,
        id: editingPatent.id,
        visible: editingPatent.visible
      };
      const result = await updatePatent(editingPatent.id, newItem);
      if (result.success) {
        setEditingPatent(null);
        setMessage('Patent updated successfully!');
      } else {
        setMessage(result.error || 'Error updating patent');
      }
    } catch (error) {
      setMessage('Error updating patent');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePatent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this patent?')) return;
    try {
      const result = await deletePatent(id);
      if (result.success) {
        setMessage('Patent deleted successfully!');
      } else {
        setMessage(result.error || 'Error deleting patent');
      }
    } catch (error) {
      setMessage('Error deleting patent');
    }
  };

  const handleImageUpload = async (file, patentId = null) => {
    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success) {
        const imageUrl = response.data.url;
        if (patentId) {
          // Update existing patent
          const updatedPatent = { ...editingPatent, image_url: imageUrl };
          setEditingPatent(updatedPatent);
        } else {
          // Update new patent form
          setNewPatent({ ...newPatent, image_url: imageUrl });
        }
        setMessage('Image uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage('Error uploading image');
    }
  };

  // Add this function for toggling visibility in the patents list and persist to backend
  // Use a single handler for all field changes, including visibility
  const handlePatentChange = async (index, field, value) => {
    const updatedPatent = { ...patents[index], [field]: value };
      await updatePatent(updatedPatent.id, updatedPatent);
    // No need to update local state, context will refresh and re-render
  };

  const { loading } = usePortfolio();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">Patents Manager</h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <FaPlus />
          Add Patent
        </motion.button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-green-500/20 text-green-300 border border-green-500/30'
          }`}
        >
          {message}
        </motion.div>
      )}

      {/* Add Patent Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-700 rounded-lg"
          >
            <h4 className="text-lg font-medium text-white mb-4">Add New Patent</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Patent Title *
                </label>
                <input
                  type="text"
                  value={newPatent.title}
                  onChange={(e) => setNewPatent({ ...newPatent, title: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="Patent Title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Date
                </label>
                <input
                  type="text"
                  value={newPatent.date}
                  onChange={(e) => setNewPatent({ ...newPatent, date: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="YYYY-MM-DD"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newPatent.description}
                  onChange={(e) => setNewPatent({ ...newPatent, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="Patent Description"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Authors (comma separated)
                </label>
                <input
                  type="text"
                  value={newPatent.authors.join(', ')}
                  onChange={(e) => setNewPatent({ ...newPatent, authors: e.target.value.split(',').map(a => a.trim()) })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="Author1, Author2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => handleImageUpload(e.target.files[0])}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                />
                {newPatent.image_url && (
                  <img src={newPatent.image_url} alt="Patent" className="mt-2 h-20 rounded" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Visibility
                </label>
                <VisibilityToggle
                  checked={newPatent.visible}
                  onChange={e => setNewPatent({ ...newPatent, visible: e.target.checked })}
                  loading={false}
                />
              </div>
            </div>
            <div className="flex gap-4 mt-4">
              <button
                onClick={handleAddPatent}
                className="btn btn-primary flex items-center gap-2"
                disabled={isSaving}
              >
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                Save
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="btn btn-secondary flex items-center gap-2"
                disabled={isSaving}
              >
                <FaTimes />
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Patent Form */}
      <AnimatePresence>
        {editingPatent && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 p-4 bg-gray-700 rounded-lg"
          >
            <h4 className="text-lg font-medium text-white mb-4">Edit Patent</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Patent Title *
                </label>
                <input
                  type="text"
                  value={editingPatent.title}
                  onChange={(e) => setEditingPatent({ ...editingPatent, title: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="Patent Title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Date
                </label>
                <input
                  type="text"
                  value={editingPatent.date}
                  onChange={(e) => setEditingPatent({ ...editingPatent, date: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="YYYY-MM-DD"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={editingPatent.description}
                  onChange={(e) => setEditingPatent({ ...editingPatent, description: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="Patent Description"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Authors (comma separated)
                </label>
                <input
                  type="text"
                  value={editingPatent.authors.join(', ')}
                  onChange={(e) => setEditingPatent({ ...editingPatent, authors: e.target.value.split(',').map(a => a.trim()) })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="Author1, Author2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={e => handleImageUpload(e.target.files[0], editingPatent.id)}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white"
                />
                {editingPatent.image_url && (
                  <img src={editingPatent.image_url} alt="Patent" className="mt-2 h-20 rounded" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Visibility
                </label>
                <VisibilityToggle
                  checked={editingPatent.visible}
                  onChange={e => setEditingPatent({ ...editingPatent, visible: e.target.checked })}
                  loading={false}
                />
              </div>
            </div>
            <div className="flex gap-4 mt-4">
              <button
                onClick={handleUpdatePatent}
                className="btn btn-primary flex items-center gap-2"
                disabled={isSaving}
              >
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                Save
              </button>
              <button
                onClick={() => setEditingPatent(null)}
                className="btn btn-secondary flex items-center gap-2"
                disabled={isSaving}
              >
                <FaTimes />
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Patents List */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Title</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Date</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Authors</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Visible</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {patents.map((patent, index) => (
              <tr key={patent.id}>
                <td className="px-4 py-2 text-white">{patent.title}</td>
                <td className="px-4 py-2 text-gray-300">{patent.date}</td>
                <td className="px-4 py-2 text-gray-300">{Array.isArray(patent.authors) ? patent.authors.join(', ') : ''}</td>
                <td className="px-4 py-2">
                  <VisibilityToggle
                    checked={patent.visible ?? true}
                    onChange={e => handlePatentChange(index, 'visible', e.target.checked)}
                    loading={!!toggleLoading[patent.id]}
                  />
                </td>
                <td className="px-4 py-2 flex gap-2">
                  <button
                    onClick={() => setEditingPatent(patent)}
                    className="btn btn-xs btn-secondary flex items-center gap-1"
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    onClick={() => handleDeletePatent(patent.id)}
                    className="btn btn-xs btn-danger flex items-center gap-1"
                  >
                    <FaTrash /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

export default PatentsEditor; 